/**
 * Cart drawer toppers rail: add-to-cart + info modal.
 * Also carries upsell order attribution for BOTH surfaces (drawer rail and
 * PDP add-on toggles) - see the attribution section below. It lives here
 * because cart-drawer.liquid renders on every page, so this file is already
 * loaded on PDPs and no extra request is needed.
 *
 * - All listeners are delegated on document, so the rail can be replaced by
 *   drawer rebuilds and the optimistic swap without rebinding.
 * - Adds go through window.SC_cartFetch (the cart queue), single-item form,
 *   so gift reconcile runs and sc-klaviyo-atc's paid-line diff sees the add.
 * - The modal is appended to document.body, NEVER inside #CartDrawer: the
 *   drawer animates with a transform, and a transformed ancestor becomes the
 *   containing block for position:fixed, clipping the modal on iOS.
 * - Body scroll lock is never touched here; the drawer owns it.
 *
 * MULTI-VARIANT TOPPERS (added 01/09/2026)
 * A card marked data-sc-topper-multi opens the modal instead of adding, and
 * the modal grows a <select> cloned from the card's
 * <template data-sc-topper-variants>. The modal's Add button takes its variant
 * id from that select, so nothing can be added without an explicit choice.
 * The picker markup is cloned per open and thrown away on the next open, so
 * the change listener never leaks and never points at a detached card - which
 * matters because the optimistic swap can replace the whole rail underneath an
 * open modal.
 *
 * WHY THE PICKER IS IN THE FOOTER AND NOT NEXT TO THE PRICE
 * The body scrolls; the footer does not. Measured on the live drawer:
 *   viewport 575px tall - under the price it fits with 34px to spare
 *   viewport 455px tall - under the price it sits 62px BELOW the fold
 * A landscape phone or a short laptop window is enough to hide it, which is
 * the exact bug this was built to fix. So the picker stays pinned above the
 * Add button, and the price is brought down to it instead: the button reads
 * "Add to cart - $16.99" and updates with the selection, so choice and price
 * are adjacent and always on screen.
 */
(function () {
  if (window.__SC_CART_TOPPERS_INIT__) return;
  window.__SC_CART_TOPPERS_INIT__ = true;

  var modal = null;
  var lastOpener = null;

  function cartFetch(url, init) {
    var f = window.SC_cartFetch || window.fetch;
    return f(url, init);
  }

  function addToCart(variantId) {
    return cartFetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    });
  }

  function isValidCart(json) {
    return window.SC_isValidCart
      ? window.SC_isValidCart(json)
      : !!(json && typeof json.item_count === 'number' && Array.isArray(json.items));
  }

  function refreshDrawer(res) {
    var done = function (cart) {
      if (cart && window.SC_emitCartRefresh) {
        window.SC_emitCartRefresh(cart);
      } else if (window.SC_emitCartBuild) {
        window.SC_emitCartBuild();
      } else {
        document.dispatchEvent(new CustomEvent('cart:build'));
      }
    };
    if (res && res.ok && res.clone) {
      res.clone().json().then(function (json) {
        done(isValidCart(json) ? json : null);
      }).catch(function () { done(null); });
    } else {
      done(null);
    }
  }

  function setBusy(btn, busy) {
    if (!btn) return;
    btn.disabled = busy;
    btn.classList.toggle('is-busy', busy);
  }

  function flashError(btn) {
    if (!btn) return;
    btn.classList.add('is-error');
    setTimeout(function () { btn.classList.remove('is-error'); }, 1600);
  }

  /**
   * ---------------- Upsell order attribution ----------------
   * Cart attributes flow onto the order and show in admin under
   * "Additional details". Two independent surfaces are tracked:
   *
   *   cart-upsell / cart-upsell-items  - added from the drawer rail
   *   pdp-upsell  / pdp-upsell-items   - added via a PDP add-on toggle
   *
   * Absence of a flag means that surface was not used, so order exports
   * filter cleanly on "attribute exists". Keys are NOT underscore-prefixed
   * on purpose: underscore attributes are hidden in parts of admin and
   * checkout. Removals are not tracked - "true" means the surface was used
   * at some point in this cart's life.
   *
   * The write goes through SC_cartFetch (the cart queue) so it serialises
   * behind the add, and so the queue caches the returned cart - which is
   * what makes the append-and-dedupe read below see prior values.
   */
  function setUpsellAttrs(flagKey, itemsKey, titles) {
    try {
      var list = Array.isArray(titles) ? titles : (titles ? [titles] : []);
      var existing = '';
      var last = window.SC_getLastCart && window.SC_getLastCart(15000);
      if (last && last.attributes && last.attributes[itemsKey]) {
        existing = String(last.attributes[itemsKey]);
      }
      var parts = existing ? existing.split(', ') : [];
      list.forEach(function (t) {
        if (t && parts.indexOf(t) === -1) parts.push(t);
      });
      var attrs = {};
      attrs[flagKey] = 'true';
      attrs[itemsKey] = parts.join(', ');
      var f = window.SC_cartFetch || window.fetch;
      f('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () {});
    } catch (e) {}
  }

  function markCartUpsell(title) {
    setUpsellAttrs('cart-upsell', 'cart-upsell-items', title);
  }

  /**
   * PDP add-on attribution.
   *
   * The PDP add-on toggles are not standalone adds - they ride along with the
   * main add to cart. That add does NOT go through window.fetch or the cart
   * queue: the theme's product form posts via raw XMLHttpRequest (verified -
   * an ATC with a toggle ticked produces two XHR POSTs to /cart/add.js and
   * zero SC_cartFetch calls). So there is nothing to wrap at the fetch layer.
   *
   * Instead: capture which toggles are ticked at the moment the ATC button is
   * clicked (capture phase, before any handler can reset them), then wait for
   * a cart event and only attribute the ones that actually landed in the cart.
   * That confirms the add succeeded without costing an extra request.
   *
   * The ticked-at-click requirement is also what stops a drawer-rail add of
   * the same product being misattributed to the PDP - several products
   * (Easy-Scraper Set, Feather-Touch Cloth) appear in both surfaces.
   */
  var pendingPdpUpsells = null;
  var pendingPdpTimer = null;

  function pdpCheckedUpsells() {
    var out = [];
    document.querySelectorAll('[data-sc-upsell-wrap] [data-upsell][data-upsell-variant-id]').forEach(function (row) {
      var check = row.querySelector('.sc-upsell-check');
      if (!check || !check.checked) return;
      var title = row.getAttribute('data-title');
      if (!title) return;
      out.push({ id: String(row.getAttribute('data-upsell-variant-id')), title: title });
    });
    return out;
  }

  function flushPdpUpsells(cart) {
    if (!pendingPdpUpsells || !cart || !cart.items) return;
    var present = Object.create(null);
    cart.items.forEach(function (i) { present[String(i.variant_id)] = true; });
    var landed = pendingPdpUpsells.filter(function (p) { return present[p.id]; });
    if (!landed.length) return;
    pendingPdpUpsells = null;
    clearTimeout(pendingPdpTimer);
    setUpsellAttrs('pdp-upsell', 'pdp-upsell-items', landed.map(function (p) { return p.title; }));
  }

  function cartFromEvent(e) {
    if (e && e.detail && e.detail.cart) return e.detail.cart;
    return (window.SC_getLastCart && window.SC_getLastCart(15000)) || null;
  }

  /* WINDOW, not document, and capture phase. Verified: something in the
     theme's ATC path stops propagation between window and document, so a
     document-level listener never sees the add-to-cart click at all. Window
     capture is the first thing to run and does fire. */
  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest
      ? e.target.closest('[data-add-to-cart], button[name="add"]')
      : null;
    if (!btn) return;
    var checked = pdpCheckedUpsells();
    if (!checked.length) return;
    pendingPdpUpsells = checked;
    clearTimeout(pendingPdpTimer);
    /* Give up if no cart event confirms the add - never attribute blind. */
    pendingPdpTimer = setTimeout(function () { pendingPdpUpsells = null; }, 15000);
  }, true);

  document.addEventListener('ajaxProduct:added', function (e) { flushPdpUpsells(cartFromEvent(e)); });
  document.addEventListener('cart:updated', function (e) { flushPdpUpsells(cartFromEvent(e)); });
  document.addEventListener('cart:build', function (e) { flushPdpUpsells(cartFromEvent(e)); });

  function handleAdd(variantId, title, btn, onSuccess) {
    if (!variantId || (btn && btn.disabled)) return;
    setBusy(btn, true);
    addToCart(variantId).then(function (res) {
      if (res && res.ok) {
        if (onSuccess) onSuccess();
        markCartUpsell(title);
        refreshDrawer(res);
        /* The rebuild replaces the rail; unbusy is a fallback in case it is skipped. */
        setTimeout(function () { setBusy(btn, false); }, 4000);
      } else {
        setBusy(btn, false);
        flashError(btn);
      }
    }).catch(function () {
      setBusy(btn, false);
      flashError(btn);
    });
  }

  /* ---------------- Modal ---------------- */

  var VARIANT_CSS =
    '.sc-topper-modal__variant-slot:empty{display:none}' +
    '.sc-topper-modal__variant{display:flex;flex-direction:column;gap:6px;margin:0 0 10px}' +
    '.sc-topper-modal__variant-label{font-size:12px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:rgba(47,72,88,.62)}' +
    '.sc-topper-modal__variant-select{width:100%;min-height:44px;padding:10px 34px 10px 12px;font:inherit;font-size:15px;color:#2F4858;background-color:#fff;border:1px solid rgba(47,72,88,.24);border-radius:8px;-webkit-appearance:none;appearance:none;' +
      'background-image:url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%232F4858\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'M6 9l6 6 6-6\'/%3E%3C/svg%3E");' +
      'background-repeat:no-repeat;background-position:right 11px center;background-size:16px 16px}' +
    '.sc-topper-modal__variant-select:focus-visible{outline:2px solid #2F4858;outline-offset:1px}' +
    '.sc-topper-modal__add-price:not(:empty){margin-left:.4em}' +
    '.sc-topper-modal__add-price:not(:empty)::before{content:"\\00b7";margin-right:.4em;opacity:.55}';

  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'sc-topper-modal';
    modal.setAttribute('data-sc-topper-modal', '');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<style>' + VARIANT_CSS + '</style>' +
      '<div class="sc-topper-modal__dialog" role="dialog" aria-modal="true" aria-label="Product details">' +
        '<button type="button" class="sc-topper-modal__close" data-sc-topper-modal-close aria-label="Close">&times;</button>' +
        '<div class="sc-topper-modal__body" data-sc-topper-modal-body></div>' +
        '<div class="sc-topper-modal__footer">' +
          '<div class="sc-topper-modal__variant-slot" data-sc-topper-variant-slot></div>' +
          '<button type="button" class="btn sc-topper-modal__add" data-sc-topper-modal-add>' +
            '<span>Add to cart</span>' +
            '<span class="sc-topper-modal__add-price" data-sc-topper-add-price></span>' +
          '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    /* Handle modal clicks HERE and stop propagation: the modal sits outside
       #CartDrawer in the DOM, so any click inside it looks like an
       "outside the drawer" click to the theme's drawer handler, which would
       close the drawer behind the modal. */
    modal.addEventListener('click', function (e) {
      e.stopPropagation();
      var t = e.target;
      if (!t || !t.closest) return;
      var modalAdd = t.closest('[data-sc-topper-modal-add]');
      if (modalAdd) {
        e.preventDefault();
        handleAdd(modalAdd.getAttribute('data-variant-id'), modalAdd.getAttribute('data-topper-title'), modalAdd, closeModal);
        return;
      }
      if (t.closest('[data-sc-topper-modal-close]') || t === modal) {
        e.preventDefault();
        closeModal();
      }
    });
    /* Same reasoning as the click handler: a change event bubbling out of the
       modal must not reach the drawer or the PDP upsell listeners. */
    modal.addEventListener('change', function (e) {
      e.stopPropagation();
      var sel = e.target && e.target.closest ? e.target.closest('[data-sc-topper-variant-select]') : null;
      if (sel) syncModalVariant(sel);
    });
    return modal;
  }

  function setAddPrice(text) {
    if (!modal) return;
    var el = modal.querySelector('[data-sc-topper-add-price]');
    if (el) el.textContent = text || '';
  }

  /* Pushes the selected variant into the modal's Add button and price row.
     Called on open and on every change, so the button can never carry a
     variant id the customer did not choose. */
  function syncModalVariant(sel) {
    if (!modal || !sel) return;
    var opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    var addBtn = modal.querySelector('[data-sc-topper-modal-add]');
    if (addBtn) {
      addBtn.setAttribute('data-variant-id', opt.value);
      addBtn.disabled = !!opt.disabled;
    }
    var price = opt.getAttribute('data-price') || '';
    var compare = opt.getAttribute('data-compare') || '';
    var nowEl = modal.querySelector('[data-sc-topper-price-now]');
    var wasEl = modal.querySelector('[data-sc-topper-price-was]');
    var saveEl = modal.querySelector('[data-sc-topper-price-save]');
    if (nowEl && price) nowEl.textContent = price;
    if (wasEl) {
      wasEl.textContent = compare;
      wasEl.hidden = !compare;
    }
    if (saveEl) saveEl.hidden = !compare;
    /* Price rides on the button so it stays beside the picker in the footer,
       where both are always on screen. */
    setAddPrice(price);
  }

  function openModal(card, opener) {
    var tpl = card.querySelector('template[data-sc-topper-body]');
    if (!tpl) return;
    var m = ensureModal();
    var body = m.querySelector('[data-sc-topper-modal-body]');
    var slot = m.querySelector('[data-sc-topper-variant-slot]');
    body.innerHTML = '';
    body.appendChild(tpl.content.cloneNode(true));
    body.scrollTop = 0;
    if (slot) slot.innerHTML = '';

    var addBtn = m.querySelector('[data-sc-topper-modal-add]');
    addBtn.setAttribute('data-topper-title', card.getAttribute('data-topper-title') || '');
    addBtn.disabled = false;
    addBtn.classList.remove('is-busy', 'is-error');

    /* Multi-variant: clone the picker into the non-scrolling footer and let it
       own the variant id. Single variant: the card's own id is the only
       possible answer. */
    var varTpl = card.querySelector('template[data-sc-topper-variants]');
    var sel = null;
    if (varTpl && slot) {
      slot.appendChild(varTpl.content.cloneNode(true));
      sel = slot.querySelector('[data-sc-topper-variant-select]');
    }
    if (sel) {
      syncModalVariant(sel);
    } else {
      addBtn.setAttribute('data-variant-id', card.getAttribute('data-variant-id') || '');
      var nowEl = body.querySelector('[data-sc-topper-price-now]');
      setAddPrice(nowEl ? nowEl.textContent.trim() : '');
    }

    lastOpener = opener || null;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    var closeBtn = m.querySelector('[data-sc-topper-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (lastOpener && document.contains(lastOpener)) {
      try { lastOpener.focus(); } catch (e) {}
    }
    lastOpener = null;
  }

  /* ---------------- Rail arrows (desktop) ---------------- */

  var SCROLL_EPSILON = 2;

  function railStep(rail) {
    var card = rail.querySelector('[data-sc-topper]');
    var cardWidth = card ? card.getBoundingClientRect().width : 132;
    return (cardWidth + 10) * 2; /* two cards per click */
  }

  function updateArrows(root) {
    var rail = root.querySelector('[data-sc-toppers-rail]');
    var prev = root.querySelector('[data-sc-toppers-prev]');
    var next = root.querySelector('[data-sc-toppers-next]');
    if (!rail || !prev || !next) return;
    var overflow = rail.scrollWidth > rail.clientWidth + SCROLL_EPSILON;
    var atStart = rail.scrollLeft <= SCROLL_EPSILON;
    var atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - SCROLL_EPSILON;
    prev.hidden = !overflow || atStart;
    next.hidden = !overflow || atEnd;
  }

  function updateAllArrows() {
    document.querySelectorAll('[data-sc-toppers]').forEach(updateArrows);
  }

  var arrowTimer1 = null;
  var arrowTimer2 = null;

  function scheduleArrowUpdate() {
    /* After rebuilds/opens the rail may have been replaced or just become
       visible (clientWidth is 0 while the drawer is closed). Debounced:
       mutation observers can fire in bursts during a rebuild. */
    clearTimeout(arrowTimer1);
    clearTimeout(arrowTimer2);
    arrowTimer1 = setTimeout(updateAllArrows, 150);
    arrowTimer2 = setTimeout(updateAllArrows, 650);
  }

  /* Cart events alone are not enough: opening the drawer fires none of them,
     and updateArrows is meaningless while the drawer is hidden (clientWidth 0).
     Watch the drawer's class for the open state and [data-products] for
     rail replacements. */
  function watchDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (drawer && !drawer.__scToppersClassObs) {
      drawer.__scToppersClassObs = true;
      new MutationObserver(function () {
        if (drawer.classList.contains('drawer--is-open')) scheduleArrowUpdate();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    var products = document.querySelector('#CartDrawer [data-products]');
    if (products && !products.__scToppersChildObs) {
      products.__scToppersChildObs = true;
      new MutationObserver(scheduleArrowUpdate).observe(products, { childList: true, subtree: true });
    }
  }

  /* scroll does not bubble; capture catches the rail's own scroll events */
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.nodeType === 1 && t.hasAttribute && t.hasAttribute('data-sc-toppers-rail')) {
      var root = t.closest('[data-sc-toppers]');
      if (root) updateArrows(root);
    }
  }, true);

  document.addEventListener('cart:updated', scheduleArrowUpdate);
  document.addEventListener('cart:build', scheduleArrowUpdate);
  document.addEventListener('cart:open', scheduleArrowUpdate);
  window.addEventListener('resize', function () {
    clearTimeout(window.__scToppersResizeT);
    window.__scToppersResizeT = setTimeout(updateAllArrows, 200);
  });
  function initArrows() {
    watchDrawer();
    scheduleArrowUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initArrows);
  } else {
    initArrows();
  }

  /* ---------------- Events ---------------- */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var arrowPrev = t.closest('[data-sc-toppers-prev]');
    var arrowNext = t.closest('[data-sc-toppers-next]');
    if (arrowPrev || arrowNext) {
      var arrowRoot = (arrowPrev || arrowNext).closest('[data-sc-toppers]');
      var rail = arrowRoot ? arrowRoot.querySelector('[data-sc-toppers-rail]') : null;
      if (rail) {
        e.preventDefault();
        rail.scrollBy({ left: arrowPrev ? -railStep(rail) : railStep(rail), behavior: 'smooth' });
        setTimeout(function () { updateArrows(arrowRoot); }, 400);
      }
      return;
    }

    var addBtn = t.closest('[data-sc-topper-add]');
    if (addBtn) {
      var card = addBtn.closest('[data-sc-topper]');
      if (!card) return;
      e.preventDefault();
      /* Multi-variant products must never be added blind - open the picker. */
      if (card.getAttribute('data-sc-topper-multi') === 'true') {
        openModal(card, addBtn);
        return;
      }
      handleAdd(card.getAttribute('data-variant-id'), card.getAttribute('data-topper-title'), addBtn, null);
      return;
    }

    var infoBtn = t.closest('[data-sc-topper-info]');
    if (infoBtn) {
      var infoCard = infoBtn.closest('[data-sc-topper]');
      if (!infoCard) return;
      e.preventDefault();
      openModal(infoCard, infoBtn);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!modal || !modal.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      closeModal();
      return;
    }
    if (e.key !== 'Tab') return;
    var focusables = modal.querySelectorAll('button:not([disabled]), a[href], select');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
})();
